import React from 'react';
import { HumanTask } from '@dreamai/types';

// Wire shape served by GET /human-tasks for accepted, idle-tracked tasks.
// Mirrors HumanTaskIdleStatus in @dreamai/types (api/human-tasks.ts); drop
// this local copy once the client depends on a release that carries it.
export type HumanTaskIdleSeverity = 'ok' | 'idle' | 'stale';

export interface HumanTaskIdleStatus {
  lastActivityAt: string;
  thresholdMinutes: number;
  silentMinutes: number;
  idleMinutes: number;
  severity: HumanTaskIdleSeverity;
  nudgedAt: string | null;
  releaseCount: number;
  workingElsewhere: boolean;
  /** Holder has a phone call in progress on this task; counts as activity. */
  onCall?: boolean;
}

export type IdleTrackedTask = HumanTask & {
  idleStatus?: HumanTaskIdleStatus | null;
  releaseCount?: number;
};

export type HumanTaskIdleResponseAction =
  | 'still_working'
  | 'blocked'
  | 'release';

export interface HumanTaskIdleResponseRequest {
  action: HumanTaskIdleResponseAction;
  note?: string;
  timeSpentMinutes?: number;
}

export interface HumanTaskIdleResponseResponse {
  success: boolean;
  action?: HumanTaskIdleResponseAction;
  releaseCount?: number;
  pending?: boolean;
  error?: string;
}

/** "5m", "1h 05m", "2d 3h". */
export const formatSilentDuration = (minutes: number): string => {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours < 24) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

export const needsAttention = (task: IdleTrackedTask): boolean =>
  task.idleStatus?.severity === 'idle' || task.idleStatus?.severity === 'stale';

export const attentionTasks = (tasks: IdleTrackedTask[]): IdleTrackedTask[] =>
  tasks
    .filter(needsAttention)
    .sort(
      (a, b) =>
        (b.idleStatus?.silentMinutes ?? 0) - (a.idleStatus?.silentMinutes ?? 0),
    );

/** Extra tile class for the idle severity, or '' while activity is recent. */
export const idleTileClass = (task: IdleTrackedTask): string => {
  const severity = task.idleStatus?.severity;
  return severity && severity !== 'ok' ? `task-row--${severity}` : '';
};

const SEVERITY_TITLE: Record<HumanTaskIdleSeverity, string> = {
  ok: 'Time since the operator last did anything visible on this task',
  idle: 'Past the idle threshold — the operator has been asked to check in',
  stale: 'Well past the idle threshold — will be handed back if unanswered',
};

/**
 * Activity pill for held tasks: how long since the holder last did anything,
 * coloured by severity, plus a marker when they have moved on to other work.
 */
export const IdleActivityPill = ({ task }: { task: IdleTrackedTask }) => {
  const idle = task.idleStatus;
  if (!idle) return null;
  return (
    <span
      className={`task-activity task-activity--${idle.severity}`}
      title={
        idle.onCall
          ? 'Operator is on a phone call for this task right now'
          : `${SEVERITY_TITLE[idle.severity]} (threshold ${idle.thresholdMinutes}m)`
      }
    >
      {idle.onCall
        ? 'on a call'
        : idle.severity === 'ok'
          ? `active ${formatSilentDuration(idle.silentMinutes)} ago`
          : `no activity ${formatSilentDuration(idle.silentMinutes)}`}
      {idle.workingElsewhere && (
        <span
          className='task-activity-elsewhere'
          title='Operator has accepted or closed another task since this one went quiet'
        >
          working another task
        </span>
      )}
      {idle.nudgedAt && (
        <span
          className='task-activity-nudged'
          title='Check-in posted in the task thread, not yet answered'
        >
          nudged
        </span>
      )}
    </span>
  );
};

/** Pending tiles: this task has been tried before and handed back. */
export const RetryBadge = ({ task }: { task: IdleTrackedTask }) => {
  const count = task.releaseCount ?? 0;
  if (task.status !== 'pending' || count < 1) return null;
  return (
    <span
      className='task-retry-badge'
      title={`Handed back to the queue ${count === 1 ? 'once' : `${count} times`} — see the thread for the previous attempt`}
    >
      Retry
    </span>
  );
};

export interface NeedsAttentionStripProps {
  tasks: IdleTrackedTask[];
  currentUserId?: string;
  isAdmin: boolean;
  releasingId: string | null;
  onOpen: (task: IdleTrackedTask) => void;
  onRelease: (task: IdleTrackedTask) => void;
}

/**
 * Top-of-page strip listing accepted tasks that have gone quiet. Holders see
 * their own to act on; admins see everyone's. Release is offered to the
 * holder and to admins — the thread is where "still on it" / "blocked" live.
 */
export const NeedsAttentionStrip = ({
  tasks,
  currentUserId,
  isAdmin,
  releasingId,
  onOpen,
  onRelease,
}: NeedsAttentionStripProps) => {
  const idleTasks = attentionTasks(tasks);
  if (idleTasks.length === 0) return null;
  const elsewhere = idleTasks.filter(
    (t) => t.idleStatus?.workingElsewhere,
  ).length;
  const summary = [
    `${idleTasks.length} task${idleTasks.length === 1 ? '' : 's'} idle`,
    elsewhere > 0 ? `${elsewhere} parked while operator works elsewhere` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className='needs-attention' role='region' aria-label='Needs attention'>
      <div className='needs-attention-header'>
        <span className='needs-attention-title'>Needs attention</span>
        <span className='needs-attention-summary'>{summary}</span>
      </div>
      <ul className='needs-attention-list'>
        {idleTasks.map((task) => {
          const idle = task.idleStatus!;
          const canRelease =
            isAdmin ||
            (currentUserId !== undefined &&
              task.acceptedByUserId === currentUserId);
          return (
            <li
              key={task.id}
              className={`needs-attention-item needs-attention-item--${idle.severity}`}
            >
              <button
                type='button'
                className='needs-attention-open'
                onClick={() => onOpen(task)}
              >
                <span className='needs-attention-task-title'>{task.title}</span>
                <span className='needs-attention-detail'>
                  {task.acceptedByUserName ?? 'Unassigned'} · no activity{' '}
                  {formatSilentDuration(idle.silentMinutes)}
                  {idle.workingElsewhere ? ' · working another task' : ''}
                  {idle.releaseCount > 0
                    ? ` · handed back ${idle.releaseCount}×`
                    : ''}
                </span>
              </button>
              {canRelease && (
                <button
                  type='button'
                  className='needs-attention-release'
                  onClick={() => onRelease(task)}
                  disabled={releasingId === task.id}
                  title='Put this task back in the queue for someone else, keeping the thread and a handoff note'
                >
                  {releasingId === task.id ? 'Releasing…' : 'Release to queue'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
